import {
  Bell,
  CheckCheck,
  File,
  Folder,
  LayoutPanelLeft,
  Layers3,
  LogOut,
  Maximize2,
  Minus,
  Search,
  Square,
  PanelsTopLeft,
  X,
} from "lucide-react";
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { appById, appByPath, type DesktopAppDefinition, type DesktopAppId } from "../desktop/apps";
import { BrowserWindowChromeProvider } from "../desktop/BrowserWindowChrome";
import { supportsMultipleWindows, windowId, type DesktopWindowState } from "../desktop/layout";
import { activeWindowId, focusWindow, openOrFocus } from "../desktop/windows";
import { DesktopWindowProvider, openDesktopWindowEvent, type DesktopWindowContext, type OpenDesktopWindowDetail } from "../desktop/windowContext";
import { useDesktopWorkArea } from "../desktop/useDesktopWorkArea";
import { clampWindowBounds, initialWindowBounds, moveWindowBounds, normalizedAppSearch, resizeWindowBounds, snappedWindowBounds, type WindowBounds, type WindowSnap } from "../desktop/windowGeometry";
import { DesktopClock } from "./DesktopClock";
import { AppActionDialog } from "./AppActionDialog";
import { AppWindowBoundary } from "./AppWindowBoundary";
import { DesktopShortcuts, type DesktopShortcutsHandle } from "./DesktopShortcuts";
import { DesktopAppIcon } from "./DesktopAppIcon";
import { WindowOverview } from "./WindowOverview";
import { CommandPalette, type CommandPaletteItem } from "./CommandPalette";
import { KeyboardShortcutsDialog, type KeyboardShortcut } from "./KeyboardShortcutsDialog";
import { useWorkspaceLayout } from "../desktop/useWorkspaceLayout";
import { DesktopDesksPanel } from "../desktop/DesktopDesksPanel";
import { captureDeskWindows, defaultDesk, hasDeskControlCharacters, maximumDesks, readDesks, restoreDeskWindows, saveDesks, windowDeskId } from "../desktop/desks";
import { requestDesktopAppClose } from "../desktop/windowClose";
import { useDesktopPreferences } from "../lib/desktopPreferences";
import {
  compiledRouteForProgram,
  listMyPrograms,
  programCatalogChangedEvent,
  type ProgramCatalogEntry,
} from "../lib/programs";
import {
  createHostDirectory,
  hostWorkspaceChangedEvent,
  listHostDirectory,
  notifyHostWorkspaceChanged,
  uploadHostFile,
  type HostEntry,
} from "../lib/hostWorkspace";
import { HostDragDownloadController } from "../lib/hostDragDownload";
import { hostPaths } from "../lib/hostPaths";
import {
  clearNotifications,
  loadNotifications,
  notificationSyncStatus,
  markAllNotificationsRead,
  markNotificationRead,
  osNotificationsChangedEvent,
  pushNotification,
  type OsNotification,
  type NotificationSyncStatus,
} from "../lib/notifications";
import { connectNotificationInbox } from "../lib/notificationPersistence";
import "./desktop-integrated.css";
import "./desktop-interactions.css";
import "./notifications.css";

const compactQuery = "(max-width: 720px)";
const desktopPath = hostPaths.desktop;
const desktopKeyboardShortcuts: KeyboardShortcut[] = [
  { id: "commands", label: "Buscar aplicativos, janelas e ações", keys: ["Ctrl / ⌘", "K"], scope: "Sistema", description: "Fora de campos de edição e sessões que capturam o teclado." },
  { id: "overview", label: "Visão geral das janelas", keys: ["Ctrl / ⌘", "Shift", "L"], scope: "Sistema" },
  { id: "open-icon", label: "Abrir o atalho selecionado", keys: ["Enter"], scope: "Área de trabalho" },
  { id: "move-icon", label: "Mover o ícone selecionado", keys: ["Alt", "Setas"], scope: "Área de trabalho", description: "Altera apenas a posição visual; não move arquivos do servidor." },
  { id: "download-icon", label: "Baixar arquivo para o computador", keys: ["Alt", "Arrastar"], scope: "Área de trabalho", description: "Segure Alt antes de arrastar o arquivo para fora do aplicativo." },
  { id: "cancel", label: "Cancelar arraste ou fechar painel", keys: ["Esc"], scope: "Sistema" },
  { id: "move-window", label: "Mover ou redimensionar a janela", keys: ["Setas"], scope: "Controles das janelas", description: "Foque o título ou o controle do canto inferior direito. Shift aumenta o passo." },
  { id: "desktop-folder", label: "Nova pasta na área de trabalho", keys: ["Ctrl", "Shift", "N"], scope: "Área de trabalho", description: "Quando nenhuma janela do OS está ativa. A pasta é criada no servidor." },
  { id: "explorer-open", label: "Abrir arquivo ou pasta selecionado", keys: ["Enter"], scope: "Explorador" },
  { id: "explorer-navigate", label: "Percorrer arquivos e colunas", keys: ["Setas"], scope: "Explorador", description: "No modo detalhes, use Ctrl+Home para alcançar os cabeçalhos de ordenação e Ctrl+End para o último item." },
  { id: "explorer-rename", label: "Renomear item selecionado", keys: ["F2"], scope: "Explorador" },
  { id: "explorer-delete", label: "Solicitar exclusão do item", keys: ["Delete"], scope: "Explorador", description: "Abre uma confirmação. A exclusão física do servidor não vai para a Lixeira do OS." },
  { id: "explorer-copy", label: "Copiar item dentro do servidor", keys: ["Ctrl", "C"], scope: "Explorador" },
  { id: "explorer-cut", label: "Recortar item dentro do servidor", keys: ["Ctrl", "X"], scope: "Explorador" },
  { id: "explorer-paste", label: "Colar item na pasta atual", keys: ["Ctrl", "V"], scope: "Explorador", description: "Usa a área de transferência interna do Explorador, não arquivos copiados de outro computador." },
  { id: "explorer-properties", label: "Propriedades do item selecionado", keys: ["Alt", "Enter"], scope: "Explorador" },
  { id: "explorer-parent", label: "Subir para a pasta anterior", keys: ["Backspace"], scope: "Explorador", description: "Com foco no conteúdo da pasta, fora de campos de edição." },
  { id: "explorer-folder", label: "Criar pasta no diretório atual", keys: ["Ctrl", "Shift", "N"], scope: "Explorador" },
  { id: "editor-save", label: "Salvar texto no arquivo aberto", keys: ["Ctrl / ⌘", "S"], scope: "Editor de arquivos", description: "Não substitui automaticamente versões conflitantes; erros mantêm o texto para revisão." },
];

export function Shell() {
  const { profile, user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { stageRef, workArea } = useDesktopWorkArea();
  const { preferences } = useDesktopPreferences(user?.id ?? "");
  const [desks, setDesks] = useState(() => readDesks(user?.id ?? ""));
  const [activeDeskId, setActiveDeskId] = useState(defaultDesk.id);
  const currentDeskId = useRef(activeDeskId);
  currentDeskId.current = activeDeskId;
  const deskContextVersion = useRef(0);
  const [desksOpen, setDesksOpen] = useState(false);
  const workspaceLayout = useWorkspaceLayout(user?.id ? activeDeskId === defaultDesk.id ? user.id : `${user.id}:desk:${activeDeskId}` : "");

  const initialWindows = useRef<DesktopWindowState[] | null>(null);
  if (initialWindows.current === null) {
    const routeApp = location.pathname === "/" ? undefined : appByPath(location.pathname);
    initialWindows.current = routeApp ? openOrFocus([], routeApp.id, workArea, defaultDesk.id, { navigationSearch: location.search }) : [];
  }

  const [windows, setWindows] = useState<DesktopWindowState[]>(initialWindows.current);
  const currentWindows = useRef(windows);
  currentWindows.current = windows;
  const [startOpen, setStartOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [compact, setCompact] = useState(() => window.matchMedia?.(compactQuery).matches ?? false);
  const [catalog, setCatalog] = useState<ProgramCatalogEntry[] | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState<{ appId: string; sequence: number } | null>(null);
  const [desktopEntries, setDesktopEntries] = useState<HostEntry[]>([]);
  const [desktopMenu, setDesktopMenu] = useState<{ x: number; y: number } | null>(null);
  const [desktopCreateOpen, setDesktopCreateOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const [layoutRestoreOpen, setLayoutRestoreOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const deferredWindowFocus = useRef<string | null>(null);
  const [desktopError, setDesktopError] = useState<string | null>(null);
  const [desktopNotice, setDesktopNotice] = useState<string | null>(null);
  const [desktopDropActive, setDesktopDropActive] = useState(false);
  const [desktopUploading, setDesktopUploading] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<OsNotification[]>([]);
  const [notificationStatus, setNotificationStatus] = useState<NotificationSyncStatus>("local");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [windowMenu, setWindowMenu] = useState<string | null>(null);
  const [movingWindow, setMovingWindow] = useState<string | null>(null);
  const gesture = useRef<{ appId: string; pointerId: number; mode: "move" | "resize"; x: number; y: number; bounds: WindowBounds } | null>(null);
  const startButton = useRef<HTMLButtonElement>(null);
  const notificationButton = useRef<HTMLButtonElement>(null);
  const startMenuRef = useRef<HTMLElement>(null);
  const desktopMenuRef = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const windowRefs = useRef(new Map<string, HTMLElement>());
  const catalogRequest = useRef(0);
  const handledRouteKey = useRef(location.key);
  const desktopDropDepth = useRef(0);
  const desktopDragDownloads = useRef(new HostDragDownloadController());
  const desktopShortcuts = useRef<DesktopShortcutsHandle>(null);
  const [desktopRestoreIds, setDesktopRestoreIds] = useState<string[]>([]);
  const availableApps = useMemo(() => {
    if (!catalog) return [];
    const seen = new Set<DesktopAppId>();
    return catalog.flatMap((program) => {
      const app = compiledAppForProgram(program);
      if (!app || seen.has(app.id)) return [];
      seen.add(app.id);
      return [app];
    });
  }, [catalog]);
  const allowedAppIds = useMemo(() => new Set(availableApps.map((app) => app.id)), [availableApps]);
  const filteredApps = availableApps.filter((app) => {
    const term = normalizedAppSearch(search);
    return !term || normalizedAppSearch(`${app.title} ${app.description}`).includes(term);
  });
  const authorizedWindows = windows.filter((item) => allowedAppIds.has(item.appId));
  const deskWindows = authorizedWindows.filter((item) => windowDeskId(item) === activeDeskId);
  const activeDesk = desks.find((desk) => desk.id === activeDeskId) ?? defaultDesk;
  const activeId = activeWindowId(deskWindows);
  const showingDesktop = desktopRestoreIds.length > 0 && activeId === null;
  const reportWindowContext = useCallback((id: string, context: DesktopWindowContext) => {
    setWindows((current) => current.map((item) => windowId(item) === id && JSON.stringify(item.context) !== JSON.stringify(context) ? { ...item, context } : item));
  }, []);

  useEffect(() => {
    if (!startOpen && !notificationsOpen && !desktopMenu && !windowMenu) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest(".start-menu, .start-button, .notification-center, .notification-button, .desktop-context-menu, .window-layout-menu, .window-layout-button")) return;
      setStartOpen(false);
      setNotificationsOpen(false);
      setDesktopMenu(null);
      setWindowMenu(null);
    };
    document.addEventListener("pointerdown", closeOutside, true);
    return () => document.removeEventListener("pointerdown", closeOutside, true);
  }, [startOpen, notificationsOpen, desktopMenu, windowMenu]);

  const loadCatalog = useCallback(async (): Promise<ProgramCatalogEntry[] | null> => {
    const request = catalogRequest.current + 1;
    catalogRequest.current = request;
    setCatalogError(null);
    try {
      const nextCatalog = await listMyPrograms();
      if (catalogRequest.current !== request) return null;
      setCatalog(nextCatalog);
      return nextCatalog;
    } catch (error) {
      if (catalogRequest.current !== request) return null;
      setCatalogError(error instanceof Error ? error.message : "Não foi possível carregar o catálogo de programas.");
      return null;
    }
  }, []);

  useEffect(() => { void loadCatalog(); }, [loadCatalog]);

  useEffect(() => {
    const refresh = () => { void loadCatalog(); };
    const refreshWhenVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    window.addEventListener(programCatalogChangedEvent, refresh);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    const interval = window.setInterval(refresh, 30_000);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener(programCatalogChangedEvent, refresh);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.clearInterval(interval);
    };
  }, [loadCatalog]);

  const loadDesktopEntries = useCallback(async () => {
    if (!allowedAppIds.has("host-files")) {
      setDesktopEntries([]);
      setDesktopError(null);
      return;
    }
    try {
      const directory = await listHostDirectory("host", desktopPath);
      setDesktopEntries([...directory.entries].sort((left, right) => Number(left.kind !== "directory") - Number(right.kind !== "directory") || left.name.localeCompare(right.name, "pt-BR")));
      setDesktopError(null);
    } catch (error) {
      setDesktopEntries([]);
      setDesktopError(error instanceof Error ? error.message : "Não foi possível carregar a Área de Trabalho.");
    }
  }, [allowedAppIds]);

  useEffect(() => {
    if (!catalog) return;
    void loadDesktopEntries();
    const refresh = () => { void loadDesktopEntries(); };
    window.addEventListener(hostWorkspaceChangedEvent, refresh);
    return () => window.removeEventListener(hostWorkspaceChangedEvent, refresh);
  }, [catalog, loadDesktopEntries]);

  useEffect(() => {
    const query = window.matchMedia?.(compactQuery);
    if (!query) return;
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const refresh = () => { setNotifications(loadNotifications()); setNotificationStatus(notificationSyncStatus()); };
    window.addEventListener(osNotificationsChangedEvent, refresh);
    window.addEventListener("storage", refresh);
    const stop = connectNotificationInbox(user?.id ?? "");
    refresh();
    return () => {
      stop();
      window.removeEventListener(osNotificationsChangedEvent, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!catalog) return;
    setWindows((current) => current.filter((item) => allowedAppIds.has(item.appId)));
  }, [allowedAppIds, catalog]);

  useEffect(() => {
    if (!catalog) return;
    const routeApp = appByPath(location.pathname);
    if (!routeApp || !allowedAppIds.has(routeApp.id)) {
      return;
    }
    // Catalog refreshes must not replay navigation and undo a newer window action.
    if (handledRouteKey.current === location.key) return;
    handledRouteKey.current = location.key;
    if (routeApp.id === "home") {
      return;
    }
    // Window actions already updated state synchronously. Their route updates
    // may arrive later; only external navigation (including Back/Forward) opens.
    if (navigationType !== "POP" && location.state?.desktopWindowAction === true) return;
    let next: DesktopWindowState[];
    try { next = openOrFocus(currentWindows.current, routeApp.id, workArea, activeDeskId, { navigationSearch: location.search }); }
    catch (reason) { setDesktopError(reason instanceof Error ? reason.message : "Não foi possível abrir a janela."); return; }
    setWindows(next);
    const id = activeWindowId(next.filter((item) => windowDeskId(item) === activeDeskId));
    if (id) setFocusRequest((current) => ({ appId: id, sequence: (current?.sequence ?? 0) + 1 }));
  }, [allowedAppIds, catalog, location.key, location.pathname, location.search, location.state, navigationType, workArea, activeDeskId]);

  useEffect(() => {
    if (!focusRequest) return;
    if (commandOpen || overviewOpen || layoutRestoreOpen || desksOpen) { deferredWindowFocus.current = focusRequest.appId; return; }
    windowRefs.current.get(focusRequest.appId)?.focus({ preventScroll: true });
  }, [focusRequest]);

  useEffect(() => {
    if (commandOpen || overviewOpen || layoutRestoreOpen || desksOpen || !deferredWindowFocus.current) return;
    windowRefs.current.get(deferredWindowFocus.current)?.focus({ preventScroll: true });
    deferredWindowFocus.current = null;
  }, [commandOpen, overviewOpen, layoutRestoreOpen, desksOpen]);

  useEffect(() => {
    if (startOpen) searchInput.current?.focus();
  }, [startOpen]);

  useEffect(() => {
    if (windowMenu) windowRefs.current.get(windowMenu)?.querySelector<HTMLButtonElement>(".window-layout-menu button")?.focus();
  }, [windowMenu]);

  useLayoutEffect(() => {
    if (!desktopMenu || !desktopMenuRef.current) return;
    const menu = desktopMenuRef.current.getBoundingClientRect();
    const stage = stageRef.current?.getBoundingClientRect();
    const right = stage?.width ? stage.right : workArea.width;
    const bottom = stage?.height ? stage.bottom : workArea.height;
    const x = Math.max(8, Math.min(desktopMenu.x, right - menu.width - 8));
    const y = Math.max(8, Math.min(desktopMenu.y, bottom - menu.height - 8));
    if (x !== desktopMenu.x || y !== desktopMenu.y) setDesktopMenu({ x, y });
  }, [desktopMenu, stageRef, workArea]);

  function navigateDesktop(path: string) {
    void navigate(path, { state: { desktopWindowAction: true } });
  }

  async function launch(app: DesktopAppDefinition, newInstance = false, navigationSearch?: string): Promise<boolean> {
    if (!allowedAppIds.has(app.id)) return false;
    const requestedDeskId = activeDeskId;
    const requestedDeskVersion = deskContextVersion.current;
    const currentCatalog = await loadCatalog();
    // Do not attach a delayed app/session launch to a desk the user already left.
    if (currentDeskId.current !== requestedDeskId || deskContextVersion.current !== requestedDeskVersion) return false;
    if (!currentCatalog?.some((program) => compiledAppForProgram(program)?.id === app.id)) return false;
    let next: DesktopWindowState[];
    try { next = openOrFocus(currentWindows.current, app.id, workArea, activeDeskId, { newInstance, navigationSearch }); }
    catch (reason) { setDesktopError(reason instanceof Error ? reason.message : "Não foi possível abrir a janela."); return false; }
    setWindows(next);
    setDesktopRestoreIds([]);
    setStartOpen(false);
    setSearch("");
    const id = activeWindowId(next.filter((item) => windowDeskId(item) === activeDeskId))!;
    setFocusRequest((current) => ({ appId: id, sequence: (current?.sequence ?? 0) + 1 }));
    navigateDesktop(`${app.path}${navigationSearch ?? ""}`);
    return true;
  }

  useEffect(() => {
    const openRequested = (event: Event) => {
      const detail = (event as CustomEvent<OpenDesktopWindowDetail>).detail;
      const app = detail && appById(detail.appId);
      if (!app || (detail.search !== undefined && (typeof detail.search !== "string" || detail.search.length > 5000))) return;
      void launch(app, detail.newInstance === true, detail.search);
    };
    window.addEventListener(openDesktopWindowEvent, openRequested);
    return () => window.removeEventListener(openDesktopWindowEvent, openRequested);
  });

  async function openDesktopEntry(entry: HostEntry) {
    const explorer = appById("host-files");
    if (!explorer || !allowedAppIds.has(explorer.id) || entry.kind === "blocked") return;
    const requestedDeskId = activeDeskId;
    const requestedDeskVersion = deskContextVersion.current;
    const currentCatalog = await loadCatalog();
    if (currentDeskId.current !== requestedDeskId || deskContextVersion.current !== requestedDeskVersion) return;
    if (!currentCatalog?.some((program) => compiledAppForProgram(program)?.id === explorer.id)) return;
    const entryPath = `${desktopPath}/${entry.name}`;
    const nextPath = entry.kind === "directory" ? entryPath : desktopPath;
    const query = new URLSearchParams({ path: nextPath });
    if (entry.kind === "file") query.set("file", entryPath);
    let next: DesktopWindowState[];
    try { next = openOrFocus(currentWindows.current, explorer.id, workArea, activeDeskId, { newInstance: true, navigationSearch: `?${query.toString()}` }); }
    catch (reason) { setDesktopError(reason instanceof Error ? reason.message : "Não foi possível abrir a janela."); return; }
    setWindows(next);
    setDesktopRestoreIds([]);
    setFocusRequest((current) => ({ appId: activeWindowId(next.filter((item) => windowDeskId(item) === activeDeskId))!, sequence: (current?.sequence ?? 0) + 1 }));
    navigateDesktop(`${explorer.path}?${query.toString()}`);
  }

  function requestDesktopFolder() {
    if (!allowedAppIds.has("host-files")) return;
    setDesktopMenu(null);
    setStartOpen(false);
    setNotificationsOpen(false);
    setWindowMenu(null);
    setDesktopCreateOpen(true);
  }

  async function createDesktopFolder(name: string) {
    setDesktopError(null);
    await createHostDirectory("host", `${desktopPath}/${name}`);
    await loadDesktopEntries();
    notifyHostWorkspaceChanged();
  }

  async function uploadDesktopFiles(files: File[]) {
    if (files.length === 0) return;
    let uploaded = 0;
    setDesktopError(null);
    setDesktopNotice(null);
    try {
      for (const [index, file] of files.entries()) {
        setDesktopUploading(`Enviando ${index + 1} de ${files.length}: ${file.name}`);
        await uploadHostFile("host", `${desktopPath}/${file.name}`, file);
        uploaded += 1;
      }
      await loadDesktopEntries();
      notifyHostWorkspaceChanged();
      setDesktopNotice(uploaded === 1 ? `“${files[0].name}” foi copiado para a Área de Trabalho.` : `${uploaded} arquivos foram copiados para a Área de Trabalho.`);
      pushNotification({ title: uploaded === 1 ? "Arquivo enviado" : "Arquivos enviados", message: uploaded === 1 ? files[0].name : `${uploaded} arquivos foram copiados para a Área de Trabalho.`, kind: "success", appPath: "/projetos" });
    } catch (error) {
      if (uploaded > 0) {
        await loadDesktopEntries();
        notifyHostWorkspaceChanged();
      }
      setDesktopError(error instanceof Error ? error.message : "Não foi possível enviar os arquivos.");
      pushNotification({ title: "Falha no envio", message: error instanceof Error ? error.message : "Não foi possível enviar os arquivos.", kind: "error", appPath: "/projetos" });
    } finally {
      setDesktopUploading(null);
    }
  }

  function prepareDesktopEntryDrag(entry: HostEntry) {
    if (entry.kind === "blocked" || entry.kind === "link") return;
    void desktopDragDownloads.current.prepare("host", `${desktopPath}/${entry.name}`).catch((error) => {
      setDesktopError(error instanceof Error ? error.message : "Não foi possível preparar o download.");
    });
  }

  function startDesktopEntryDrag(event: ReactDragEvent<HTMLButtonElement>, entry: HostEntry) {
    if (entry.kind === "blocked" || entry.kind === "link") {
      event.preventDefault();
      return;
    }
    const entryPath = `${desktopPath}/${entry.name}`;
    if (desktopDragDownloads.current.start(event.dataTransfer, "host", entryPath)) return;
    event.preventDefault();
    setDesktopNotice("Preparando o download. Arraste o item novamente em um instante.");
    void desktopDragDownloads.current.prepare("host", entryPath).catch((error) => {
      setDesktopError(error instanceof Error ? error.message : "Não foi possível preparar o download.");
    });
  }

  function enterDesktopDrop(event: ReactDragEvent<HTMLElement>) {
    if (!hasExternalFiles(event.dataTransfer) || (event.target as HTMLElement).closest(".desktop-window")) return;
    event.preventDefault();
    desktopDropDepth.current += 1;
    setDesktopDropActive(true);
  }

  function leaveDesktopDrop(event: ReactDragEvent<HTMLElement>) {
    if (!hasExternalFiles(event.dataTransfer)) return;
    desktopDropDepth.current = Math.max(0, desktopDropDepth.current - 1);
    if (desktopDropDepth.current === 0) setDesktopDropActive(false);
  }

  function dropOnDesktop(event: ReactDragEvent<HTMLElement>) {
    if (!hasExternalFiles(event.dataTransfer) || (event.target as HTMLElement).closest(".desktop-window")) return;
    event.preventDefault();
    desktopDropDepth.current = 0;
    setDesktopDropActive(false);
    void uploadDesktopFiles(Array.from(event.dataTransfer.files));
  }

  function focus(appId: string) {
    const item = windows.find((item) => windowId(item) === appId);
    const app = item && appById(item.appId);
    if (!app || !allowedAppIds.has(app.id)) return;
    setWindows((current) => focusWindow(current, appId, activeDeskId));
    setDesktopRestoreIds([]);
    setFocusRequest((current) => ({ appId, sequence: (current?.sequence ?? 0) + 1 }));
    navigateDesktop(app.path);
  }

  function activateFromWindow(appId: string) {
    const item = windows.find((item) => windowId(item) === appId);
    const app = item && appById(item.appId);
    if (!app || !allowedAppIds.has(app.id)) return;
    setWindows((current) => focusWindow(current, appId, activeDeskId));
    setDesktopRestoreIds([]);
    if (location.pathname !== app.path) {
      navigateDesktop(app.path);
    }
  }

  function minimize(appId: string) {
    setWindowMenu(null);
    const nextWindows = windows.map((item) => windowId(item) === appId ? { ...item, minimized: true } : item);
    setWindows(nextWindows);
    const next = [...nextWindows].filter((item) => !item.minimized && windowDeskId(item) === activeDeskId).sort((a, b) => b.z - a.z)[0];
    const nextApp = next && appById(next.appId);
    if (nextApp) {
      focus(windowId(next));
    } else {
      navigateDesktop("/");
    }
  }

  function toggleMaximize(appId: string) {
    setWindowMenu(null);
    setWindows((current) => current.map((item) => windowId(item) === appId ? { ...item, maximized: !item.maximized, minimized: false, snap: null } : item));
  }

  function arrangeWindow(appId: string, snap: WindowSnap | null) {
    setWindows((current) => current.map((item) => windowId(item) === appId ? { ...item, snap, maximized: false, minimized: false } : item));
    setWindowMenu(null);
    focus(appId);
  }

  function beginWindowGesture(event: ReactPointerEvent<HTMLElement>, item: DesktopWindowState) {
    if (compact || item.maximized || item.snap || event.button !== 0 || !(event.target instanceof Element)) return;
    const resize = Boolean(event.target.closest(".window-resize-handle"));
    const titlebar = event.target.closest(".window-titlebar, .browser-toolbar");
    if (!resize && (!titlebar || event.target.closest("button, input, select, textarea, a, .window-layout-menu"))) return;
    const bounds = clampWindowBounds(item.bounds ?? initialWindowBounds(workArea), workArea);
    gesture.current = { appId: windowId(item), pointerId: event.pointerId, mode: resize ? "resize" : "move", x: event.clientX, y: event.clientY, bounds };
    setMovingWindow(windowId(item));
    activateFromWindow(windowId(item));
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  function updateWindowGesture(event: ReactPointerEvent<HTMLElement>) {
    const active = gesture.current;
    if (!active || active.pointerId !== event.pointerId) return;
    const bounds = (active.mode === "move" ? moveWindowBounds : resizeWindowBounds)(active.bounds, event.clientX - active.x, event.clientY - active.y, workArea);
    setWindows((current) => current.map((item) => windowId(item) === active.appId ? { ...item, bounds } : item));
  }

  function finishWindowGesture(event: ReactPointerEvent<HTMLElement>) {
    if (gesture.current?.pointerId !== event.pointerId) return;
    gesture.current = null;
    setMovingWindow(null);
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  function adjustWindowWithKeyboard(event: ReactKeyboardEvent<HTMLElement>, item: DesktopWindowState, mode: "move" | "resize") {
    if (compact || item.maximized || item.snap || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 40 : 10;
    const dx = event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0;
    const dy = event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
    const bounds = (mode === "move" ? moveWindowBounds : resizeWindowBounds)(clampWindowBounds(item.bounds ?? initialWindowBounds(workArea), workArea), dx, dy, workArea);
    setWindows((current) => current.map((candidate) => windowId(candidate) === windowId(item) ? { ...candidate, bounds } : candidate));
  }

  function close(appId: string) {
    const closing = windows.find((item) => windowId(item) === appId);
    if (!closing || !requestDesktopAppClose(closing.appId, appId)) return;
    setWindowMenu(null);
    const remaining = windows.filter((item) => windowId(item) !== appId);
    setWindows(remaining);
    const next = [...remaining].filter((item) => !item.minimized && windowDeskId(item) === activeDeskId).sort((a, b) => b.z - a.z)[0];
    const nextApp = next && appById(next.appId);
    if (nextApp) focus(windowId(next));
    else navigateDesktop("/");
  }

  function toggleDesktop() {
    setStartOpen(false);
    setNotificationsOpen(false);
    setWindowMenu(null);
    if (showingDesktop) {
      const nextWindows = windows.map((item) => desktopRestoreIds.includes(windowId(item)) ? { ...item, minimized: false } : item);
      const nextId = activeWindowId(nextWindows.filter((item) => allowedAppIds.has(item.appId) && windowDeskId(item) === activeDeskId));
      setWindows(nextWindows);
      setDesktopRestoreIds([]);
      if (nextId) focus(nextId);
      return;
    }
    const visibleIds = deskWindows.filter((item) => !item.minimized).map(windowId);
    if (visibleIds.length === 0) return;
    setDesktopRestoreIds(visibleIds);
    setWindows((current) => current.map((item) => windowDeskId(item) === activeDeskId ? { ...item, minimized: true } : item));
    navigateDesktop("/");
    startButton.current?.focus();
  }

  function handleKeyboard(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.defaultPrevented || event.nativeEvent.isComposing || desktopCreateOpen || commandOpen || overviewOpen || layoutRestoreOpen || shortcutsOpen || desksOpen) return;
    const editing = event.target instanceof Element && event.target.closest("input, textarea, select, [contenteditable=true]");
    if (!editing && (event.ctrlKey || event.metaKey)) {
      if (!event.shiftKey && event.key.toLocaleLowerCase() === "k") { event.preventDefault(); openCommands(); return; }
      if (event.shiftKey && event.key.toLocaleLowerCase() === "l") { event.preventDefault(); openOverview(); return; }
    }
    if (event.altKey && event.key === "F4" && activeId) {
      event.preventDefault();
      close(activeId);
    }
    if (event.key === "Escape" && (startOpen || notificationsOpen || desktopMenu || windowMenu)) {
      event.preventDefault();
      if (startOpen) startButton.current?.focus();
      else if (notificationsOpen) notificationButton.current?.focus();
      else if (windowMenu) windowRefs.current.get(windowMenu)?.querySelector<HTMLButtonElement>(".window-layout-button")?.focus();
      setStartOpen(false);
      setNotificationsOpen(false);
      setDesktopMenu(null);
      setWindowMenu(null);
      setSearch("");
    }
    if (event.ctrlKey && event.shiftKey && event.key.toLocaleLowerCase() === "n" && !activeId) {
      event.preventDefault();
      requestDesktopFolder();
    }
  }

  function handleWindowMenuKeyboard(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>("[role=menuitem]"));
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
  }

  function closePanels() {
    setStartOpen(false); setNotificationsOpen(false); setDesktopMenu(null); setWindowMenu(null);
  }
  function openCommands() { closePanels(); setOverviewOpen(false); setCommandOpen(true); }
  function openOverview() { closePanels(); setCommandOpen(false); setOverviewOpen(true); }
  function openKeyboardShortcuts() { closePanels(); setCommandOpen(false); setOverviewOpen(false); setShortcutsOpen(true); }
  function openDesks() { closePanels(); setCommandOpen(false); setOverviewOpen(false); setDesksOpen(true); }
  function switchDesk(id: string) {
    if (!desks.some((desk) => desk.id === id)) return;
    if (currentDeskId.current !== id) deskContextVersion.current += 1;
    currentDeskId.current = id;
    setActiveDeskId(id);
    setDesktopRestoreIds([]);
    setWindowMenu(null);
    const nextId = activeWindowId(authorizedWindows.filter((item) => windowDeskId(item) === id));
    if (nextId) setFocusRequest((current) => ({ appId: nextId, sequence: (current?.sequence ?? 0) + 1 }));
    navigateDesktop(nextId ? appById(windows.find((item) => windowId(item) === nextId)!.appId)!.path : "/");
  }
  function createDesk(value: string) {
    const name = value.trim();
    if (!name || name.length > 48 || hasDeskControlCharacters(name)) throw new Error("Use um nome de até 48 caracteres para a mesa.");
    if (desks.length >= maximumDesks) throw new Error(`Você pode manter até ${maximumDesks} mesas neste navegador.`);
    if (desks.some((desk) => normalizedAppSearch(desk.name) === normalizedAppSearch(name))) throw new Error("Já existe uma mesa com esse nome.");
    const desk = { id: crypto.randomUUID(), name };
    const next = [...desks, desk];
    if (!saveDesks(user?.id ?? "", next)) throw new Error("Não foi possível salvar a mesa neste navegador. Nenhuma mesa foi criada.");
    setDesks(next);
    deskContextVersion.current += 1;
    currentDeskId.current = desk.id;
    setActiveDeskId(desk.id);
    setDesktopRestoreIds([]);
    navigateDesktop("/");
  }
  function renameDesk(value: string) {
    const name = value.trim();
    if (activeDeskId === defaultDesk.id || !name || name.length > 48 || hasDeskControlCharacters(name)) throw new Error("Use um nome de até 48 caracteres para uma mesa criada por você.");
    if (desks.some((desk) => desk.id !== activeDeskId && normalizedAppSearch(desk.name) === normalizedAppSearch(name))) throw new Error("Já existe uma mesa com esse nome.");
    const next = desks.map((desk) => desk.id === activeDeskId ? { ...desk, name } : desk);
    if (!saveDesks(user?.id ?? "", next)) throw new Error("Não foi possível salvar o novo nome neste navegador.");
    setDesks(next);
  }
  async function restoreDeskSnapshot(source: unknown) {
    const requestedDeskId = currentDeskId.current;
    const requestedDeskVersion = deskContextVersion.current;
    const currentCatalog = await loadCatalog();
    if (!currentCatalog) throw new Error("Não foi possível confirmar os acessos. Nenhum aplicativo foi restaurado.");
    if (currentDeskId.current !== requestedDeskId || deskContextVersion.current !== requestedDeskVersion) throw new Error("A mesa ativa mudou durante a conferência. Solicite a restauração novamente.");
    const allowed = new Set(currentCatalog.flatMap((program) => { const app = compiledAppForProgram(program); return app ? [app.id] : []; }));
    const result = restoreDeskWindows(currentWindows.current, source, allowed, workArea, activeDeskId);
    setWindows(result.windows.filter((item) => allowed.has(item.appId)));
    setDesktopRestoreIds([]);
    const nextId = activeWindowId(result.windows.filter((item) => allowed.has(item.appId) && windowDeskId(item) === activeDeskId));
    if (nextId) setFocusRequest((current) => ({ appId: nextId, sequence: (current?.sequence ?? 0) + 1 }));
    navigateDesktop(nextId ? appById(result.windows.find((item) => windowId(item) === nextId)!.appId)!.path : "/");
    return `${result.restored} janelas organizadas na mesa ${activeDesk.name}. Nenhuma janela existente foi fechada.${result.skipped.length ? ` Abra manualmente: ${result.skipped.join(", ")}.` : ""}`;
  }
  function saveWorkspace() {
    if (!workspaceLayout.save(deskWindows)) throw new Error("Não foi possível salvar a disposição neste navegador.");
    setDesktopNotice("Disposição das janelas salva para esta conta neste navegador.");
  }
  function requestWorkspaceRestore() { closePanels(); setOverviewOpen(false); setCommandOpen(false); setLayoutRestoreOpen(true); }
  async function restoreWorkspace() {
    const next = workspaceLayout.restore(deskWindows, allowedAppIds, workArea).map((item) => ({ ...item, deskId: activeDeskId }));
    setWindows((current) => [...current.filter((item) => windowDeskId(item) !== activeDeskId), ...next]);
    setDesktopRestoreIds([]);
    const nextId = activeWindowId(next);
    if (nextId) {
      setFocusRequest((current) => ({ appId: nextId, sequence: (current?.sequence ?? 0) + 1 }));
      navigateDesktop(appById(next.find((item) => windowId(item) === nextId)!.appId)!.path);
    } else navigateDesktop("/");
    setDesktopNotice("Disposição restaurada somente nas janelas já abertas. Nenhuma sessão nova foi iniciada.");
  }

  const commandItems: CommandPaletteItem[] = [
    ...availableApps.map((app): CommandPaletteItem => ({ id: `app:${app.id}`, title: app.title, description: app.description, category: "Aplicativos", icon: <DesktopAppIcon app={app} />, onSelect: async () => {
      if (!await launch(app)) throw new Error("Aplicativo indisponível ou acesso não autorizado. Atualize e tente novamente.");
    } })),
    ...deskWindows.flatMap((item): CommandPaletteItem[] => {
      const app = appById(item.appId);
      return app ? [{ id: `window:${windowId(item)}`, title: `Ativar ${app.title}`, description: item.minimized ? "Janela minimizada" : "Janela aberta", category: "Janelas abertas", icon: <app.icon size={20} />, onSelect: () => focus(windowId(item)) }] : [];
    }),
    { id: "desktop:overview", title: "Visão geral das janelas", category: "Área de trabalho", shortcut: "Ctrl ⇧ L", onSelect: openOverview },
    { id: "desktop:desks", title: "Mesas de trabalho", description: "Organizar contextos e salvar disposições entre computadores", category: "Área de trabalho", onSelect: openDesks },
    { id: "desktop:show", title: showingDesktop ? "Restaurar janelas" : "Mostrar área de trabalho", category: "Área de trabalho", onSelect: toggleDesktop },
    { id: "desktop:organize", title: "Organizar ícones por nome", category: "Área de trabalho", onSelect: () => desktopShortcuts.current?.organize() },
    { id: "desktop:reset", title: "Restaurar disposição dos ícones", category: "Área de trabalho", onSelect: () => desktopShortcuts.current?.reset() },
    { id: "workspace:save", title: "Salvar disposição das janelas", description: "Guardar posições e estados neste navegador", category: "Área de trabalho", onSelect: saveWorkspace },
    { id: "workspace:restore", title: "Restaurar disposição das janelas", description: "Somente nas janelas que já estão abertas", category: "Área de trabalho", onSelect: requestWorkspaceRestore },
    { id: "desktop:shortcuts", title: "Atalhos de teclado", description: "Como navegar, organizar e baixar arquivos", category: "Área de trabalho", onSelect: openKeyboardShortcuts },
  ];

  return (
    <div className={compact ? "desktop-shell compact-shell" : "desktop-shell"} data-wallpaper={preferences.wallpaper} data-reduce-motion={preferences.reduceMotion} onKeyDown={handleKeyboard}>
      <main
        ref={stageRef}
        className="desktop-stage"
        aria-label="Área de trabalho Base Sul OS"
        onDragEnter={enterDesktopDrop}
        onDragLeave={leaveDesktopDrop}
        onDragOver={(event) => { if (hasExternalFiles(event.dataTransfer) && !(event.target as HTMLElement).closest(".desktop-window")) { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; } }}
        onDrop={dropOnDesktop}
        onClick={() => setDesktopMenu(null)}
        onContextMenu={(event) => {
          if ((event.target as HTMLElement).closest(".desktop-window, .desktop-shortcut, .desktop-icon-pages")) return;
          event.preventDefault();
          setStartOpen(false);
          setNotificationsOpen(false);
          setWindowMenu(null);
          setDesktopMenu({ x: event.clientX, y: event.clientY });
        }}
      >
        <div className="desktop-brand" aria-hidden="true"><img src="/brand/base-sul-logo.png" alt="" /><strong>OS</strong></div>
        <DesktopShortcuts
          key={user?.id ?? "anonymous"} ref={desktopShortcuts} userId={user?.id ?? ""}
          apps={availableApps} entries={desktopEntries} workArea={workArea} density={preferences.iconDensity}
          onLaunch={(app) => void launch(app)} onOpenEntry={(entry) => void openDesktopEntry(entry)}
          onPrepareExport={prepareDesktopEntryDrag} onExport={startDesktopEntryDrag}
        />

        {desktopDropActive && <div className="desktop-drop-overlay" role="status"><File size={42} /><strong>Solte para copiar para a Área de Trabalho</strong><span>Os arquivos serão enviados para o servidor Base Sul.</span></div>}
        {desktopUploading && <div className="desktop-transfer-toast" role="status">{desktopUploading}</div>}
        {desktopNotice && <div className="desktop-transfer-toast success" role="status">{desktopNotice}<button type="button" aria-label="Fechar aviso" onClick={() => setDesktopNotice(null)}><X size={15} /></button></div>}
        {desktopError && <div className="desktop-error-toast" role="alert">{desktopError}<button type="button" onClick={() => { setDesktopError(null); void loadDesktopEntries(); }}>Tentar novamente</button></div>}
        {desktopMenu && <div ref={desktopMenuRef} className="desktop-context-menu" role="menu" style={{ left: desktopMenu.x, top: desktopMenu.y, maxHeight: Math.max(0, workArea.height - 16), overflowY: "auto" }} onClick={(event) => event.stopPropagation()}>
          <button type="button" role="menuitem" disabled={!allowedAppIds.has("host-files")} onClick={requestDesktopFolder}><Folder size={17} />Nova pasta</button>
          <button type="button" role="menuitem" onClick={() => { setDesktopMenu(null); void loadDesktopEntries(); }}><span className="context-refresh" aria-hidden="true">↻</span>Atualizar</button>
          <button type="button" role="menuitem" onClick={() => { desktopShortcuts.current?.organize(); setDesktopMenu(null); }}>Organizar ícones por nome</button>
          <button type="button" role="menuitem" onClick={() => { desktopShortcuts.current?.reset(); setDesktopMenu(null); }}>Restaurar disposição dos ícones</button>
          <button type="button" role="menuitem" onClick={openKeyboardShortcuts}>Atalhos de teclado</button>
          <button type="button" role="menuitem" onClick={openDesks}>Mesas de trabalho</button>
        </div>}

        {!catalog && !catalogError && <section className="desktop-catalog-state" role="status"><span className="catalog-spinner" aria-hidden="true" /><strong>Carregando programas…</strong></section>}
        {catalogError && <section className="desktop-catalog-state error-state" role="alert"><strong>Catálogo indisponível</strong><span>{catalogError}</span><button className="secondary-button" type="button" onClick={() => void loadCatalog()}>Tentar novamente</button></section>}
        {catalog && availableApps.length === 0 && <section className="desktop-catalog-state" role="status"><strong>Nenhum programa disponível</strong><span>Peça ao administrador para revisar seus acessos.</span></section>}

        <section className="window-layer" aria-label="Janelas abertas">
          {authorizedWindows.map((windowState) => {
            const app = appById(windowState.appId);
            if (!app) return null;
            const AppComponent = app.component;
            const id = windowId(windowState);
            const active = activeId === id && !windowState.minimized;
            const hidden = windowDeskId(windowState) !== activeDeskId || windowState.minimized || (compact && !active);
            const browser = app.id === "host-browser";
            const bounds = compact || windowState.maximized
              ? { x: 0, y: 0, width: workArea.width, height: workArea.height }
              : windowState.snap ? snappedWindowBounds(workArea, windowState.snap)
              : clampWindowBounds(windowState.bounds ?? initialWindowBounds(workArea), workArea);
            const controls = <div className="window-controls">
              {supportsMultipleWindows(app.id) && <button type="button" aria-label={`Nova janela de ${app.title}`} title="Abrir outra janela independente" onClick={(event) => { event.stopPropagation(); void launch(app, true); }}><PanelsTopLeft size={15} /></button>}
              {!compact && <button className="window-layout-button" type="button" aria-label={`Organizar ${app.title}`} title="Organizar janela" aria-expanded={windowMenu === id} aria-haspopup="menu" onClick={(event) => { event.stopPropagation(); setWindowMenu((current) => current === id ? null : id); setStartOpen(false); setNotificationsOpen(false); }}><LayoutPanelLeft size={15} /></button>}
              <button type="button" aria-label={`Minimizar ${app.title}`} title={`Minimizar ${app.title}`} onClick={(event) => { event.stopPropagation(); minimize(id); }}><Minus size={16} /></button>
              <button type="button" aria-label={windowState.maximized ? `Restaurar ${app.title}` : `Maximizar ${app.title}`} title={windowState.maximized ? `Restaurar ${app.title}` : `Maximizar ${app.title}`} onClick={(event) => { event.stopPropagation(); toggleMaximize(id); }}>{windowState.maximized ? <Square size={13} /> : <Maximize2 size={14} />}</button>
              <button className="window-close" type="button" aria-label={`Fechar ${app.title}`} title={`Fechar ${app.title}`} onClick={(event) => { event.stopPropagation(); close(id); }}><X size={17} /></button>
            </div>;
            return (
              <article
                key={id}
                data-window-id={id}
                ref={(element) => { if (element) windowRefs.current.set(id, element); else windowRefs.current.delete(id); }}
                className={`desktop-window${browser ? " browser-desktop-window" : ""}${active ? " is-active" : ""}${windowState.maximized ? " is-maximized" : ""}${windowState.snap ? " is-snapped" : ""}${movingWindow === id ? " is-moving" : ""}`}
                style={{ left: bounds.x, top: bounds.y, width: bounds.width, height: bounds.height, zIndex: 10 + windowState.z } as CSSProperties}
                role="dialog"
                aria-modal="false"
                aria-label={browser ? app.title : undefined}
                aria-labelledby={browser ? undefined : `window-title-${id}`}
                aria-hidden={hidden}
                hidden={hidden}
                tabIndex={-1}
                onMouseDown={() => activateFromWindow(id)}
                onFocusCapture={() => { if (!hidden && !active) activateFromWindow(id); }}
                onPointerDown={(event) => beginWindowGesture(event, windowState)}
                onPointerMove={updateWindowGesture}
                onPointerUp={finishWindowGesture}
                onPointerCancel={finishWindowGesture}
                onLostPointerCapture={finishWindowGesture}
              >
                {!browser && <header className="window-titlebar" onDoubleClick={(event) => { if (!(event.target as Element).closest("button")) toggleMaximize(id); }}>
                  <div className="window-drag-handle" tabIndex={0} role="group" aria-label={`Mover ${app.title}`} title="Arraste para mover; use as setas quando focado" onKeyDown={(event) => adjustWindowWithKeyboard(event, windowState, "move")}><app.icon size={16} aria-hidden="true" /><strong id={`window-title-${id}`}>{app.title}</strong></div>
                  {controls}
                </header>}
                <div className="window-content"><DesktopWindowProvider windowId={id} appId={app.id} navigationSearch={windowState.navigationSearch} initialContext={windowState.context} onContext={reportWindowContext}><BrowserWindowChromeProvider value={browser ? { controls, maximized: windowState.maximized, toggleMaximize: () => toggleMaximize(id) } : null}><AppWindowBoundary title={app.title} onClose={() => close(id)}><Suspense fallback={<section className="desktop-catalog-state"><strong>Carregando aplicativo…</strong></section>}><AppComponent /></Suspense></AppWindowBoundary></BrowserWindowChromeProvider></DesktopWindowProvider></div>
                {windowMenu === id && <div className="window-layout-menu" role="menu" aria-label={`Organizar janela ${app.title}`} onKeyDown={handleWindowMenuKeyboard}>
                  <button type="button" role="menuitem" onClick={() => arrangeWindow(id, "left")}>Metade esquerda</button>
                  <button type="button" role="menuitem" onClick={() => arrangeWindow(id, "right")}>Metade direita</button>
                  <button type="button" role="menuitem" onClick={() => arrangeWindow(id, null)}>Janela flutuante</button>
                  <button type="button" role="menuitem" onClick={() => toggleMaximize(id)}>{windowState.maximized ? "Restaurar tamanho" : "Maximizar"}</button>
                </div>}
                {!compact && !windowState.maximized && !windowState.snap && <button className="window-resize-handle" type="button" aria-label={`Redimensionar ${app.title}`} title="Arraste para redimensionar; use as setas quando focado" onKeyDown={(event) => adjustWindowWithKeyboard(event, windowState, "resize")} />}
              </article>
            );
          })}
        </section>
      </main>

      {desktopCreateOpen && <AppActionDialog
        title="Nova pasta na Área de Trabalho"
        description="A pasta será criada na Área de Trabalho do servidor, não neste computador."
        confirmLabel="Criar pasta"
        input={{ label: "Nome da pasta", initialValue: "" }}
        onConfirm={createDesktopFolder}
        onClose={() => setDesktopCreateOpen(false)}
      />}

      {commandOpen && <CommandPalette items={commandItems} onClose={() => setCommandOpen(false)} />}
      {shortcutsOpen && <KeyboardShortcutsDialog shortcuts={desktopKeyboardShortcuts} onClose={() => setShortcutsOpen(false)} />}
      {overviewOpen && <WindowOverview windows={deskWindows} activeId={activeId}
        onActivate={(id) => { setOverviewOpen(false); focus(id); }} onMinimize={minimize} onCloseWindow={close}
        onSave={() => { try { saveWorkspace(); setOverviewOpen(false); } catch (error) { setDesktopError(error instanceof Error ? error.message : "Não foi possível salvar a disposição."); setOverviewOpen(false); } }}
        onRestore={requestWorkspaceRestore} onClose={() => setOverviewOpen(false)}
      />}
      {layoutRestoreOpen && <AppActionDialog title="Restaurar disposição das janelas?" description="Serão ajustadas somente posições e estados das janelas já abertas e permitidas para esta conta. Navegador, Terminal e Codex não serão iniciados. Janelas fechadas permanecerão fechadas." confirmLabel="Restaurar disposição" onConfirm={restoreWorkspace} onClose={() => setLayoutRestoreOpen(false)} />}
      {desksOpen && <DesktopDesksPanel desks={desks} activeDeskId={activeDeskId}
        windowCounts={Object.fromEntries(desks.map((desk) => [desk.id, authorizedWindows.filter((item) => windowDeskId(item) === desk.id).length]))}
        capture={(includeContext) => captureDeskWindows(deskWindows, workArea, includeContext)} onCreate={createDesk} onRename={renameDesk}
        onSelect={switchDesk} onRestore={restoreDeskSnapshot} onClose={() => setDesksOpen(false)} />}

      {startOpen && (
        <section ref={startMenuRef} className="start-menu" id="base-sul-start-menu" aria-label="Menu Iniciar">
          <div className="start-search"><Search size={18} aria-hidden="true" /><input ref={searchInput} value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.nativeEvent.isComposing) return; if (event.key === "Enter" && filteredApps[0]) { event.preventDefault(); void launch(filteredApps[0]); } if (event.key === "ArrowDown") { event.preventDefault(); startMenuRef.current?.querySelector<HTMLButtonElement>(".start-apps button")?.focus(); } }} aria-label="Buscar aplicativos" placeholder="Buscar aplicativos" /></div>
          <p className="start-label">Aplicativos</p>
          <div className="start-apps">
            {filteredApps.map((app) => <button key={app.id} type="button" onClick={() => void launch(app)}><span><DesktopAppIcon app={app} /></span><span><strong>{app.title}</strong><small>{app.description}</small></span></button>)}
            {filteredApps.length === 0 && <p className="start-empty">Nenhum aplicativo encontrado.</p>}
          </div>
          <footer>
            <div className="start-user"><span>{initials(profile?.display_name ?? user?.email)}</span><div><strong>{profile?.display_name ?? "Usuário"}</strong><small>{user?.email}</small></div></div>
            <button className="start-signout" type="button" onClick={() => void signOut()}><LogOut size={18} /><span>Sair</span></button>
          </footer>
        </section>
      )}

      {notificationsOpen && <aside className="notification-center" aria-label="Central de notificações">
        <header><div><Bell size={18} /><strong>Notificações</strong></div><button type="button" onClick={() => { markAllNotificationsRead(); setNotifications(loadNotifications()); }} title="Marcar todas como lidas"><CheckCheck size={17} /></button></header>
        <p className="notification-sync-state" role="status">{notificationStatus === "synced" ? "Sincronizadas entre seus dispositivos · últimas 100" : notificationStatus === "syncing" ? "Sincronizando…" : notificationStatus === "offline" ? "Sincronização pendente. Avisos e leituras locais aguardam confirmação do servidor." : "Avisos disponíveis somente neste dispositivo."}</p>
        <div className="notification-list">
          {notifications.length === 0 && <p>Nenhuma notificação.</p>}
          {notifications.map((item) => <button type="button" className={`${item.kind}${item.read ? " read" : ""}`} key={item.id} onClick={() => { markNotificationRead(item.eventKey); if (item.appPath) void navigate(item.appPath); setNotificationsOpen(false); }}><span /><div><strong>{item.title}</strong><small>{item.message}</small><time dateTime={new Date(item.createdAt).toISOString()}>{new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(item.createdAt)}</time></div></button>)}
        </div>
        {notifications.length > 0 && <footer><button type="button" onClick={() => { clearNotifications(); setNotifications([]); }}>Limpar notificações</button></footer>}
      </aside>}

      <footer className="taskbar" aria-label="Barra de tarefas">
        <button ref={startButton} className="start-button" type="button" aria-label="Iniciar" aria-expanded={startOpen} aria-controls="base-sul-start-menu" onClick={() => { setStartOpen((open) => !open); setNotificationsOpen(false); setWindowMenu(null); setDesktopMenu(null); }}><img src="/brand/base-sul-monogram.png" alt="" /><span>Base Sul</span></button>
        <button className="taskbar-tool" type="button" aria-label="Buscar no sistema" title="Buscar no sistema (Ctrl / ⌘ K)" aria-expanded={commandOpen} onClick={openCommands}><Search size={19} /></button>
        <button className="taskbar-tool" type="button" aria-label="Janelas abertas" title="Janelas abertas (Ctrl / ⌘ Shift L)" aria-expanded={overviewOpen} onClick={openOverview}><PanelsTopLeft size={19} /></button>
        <button className="taskbar-tool taskbar-desks" type="button" aria-label="Mesas de trabalho" title={`Mesa atual: ${activeDesk.name}`} aria-expanded={desksOpen} onClick={openDesks}><Layers3 size={19} /><span className="taskbar-desk-name">{activeDesk.name}</span></button>
        <div className="taskbar-apps" aria-label="Aplicativos abertos">
          {deskWindows.map((item) => {
            const app = appById(item.appId);
            if (!app) return null;
            const id = windowId(item);
            return <button key={id} className={activeId === id && !item.minimized ? "taskbar-app active" : "taskbar-app"} type="button" aria-label={`${item.minimized ? "Restaurar" : "Focar"} ${app.title}`} title={app.title} aria-pressed={activeId === id && !item.minimized} onClick={() => activeId === id && !item.minimized ? minimize(id) : focus(id)}><app.icon size={18} /><span>{app.title}</span></button>;
          })}
        </div>
        <div className="taskbar-status"><button ref={notificationButton} className="notification-button" type="button" aria-label="Abrir notificações" aria-expanded={notificationsOpen} onClick={() => { setNotificationsOpen((open) => !open); setStartOpen(false); setWindowMenu(null); setDesktopMenu(null); }}><Bell size={17} />{notifications.some((item) => !item.read) && <span>{Math.min(9, notifications.filter((item) => !item.read).length)}</span>}</button><DesktopClock /></div>
        <button className="taskbar-show-desktop" type="button" aria-label={showingDesktop ? "Restaurar janelas" : "Mostrar área de trabalho"} title={showingDesktop ? "Restaurar janelas" : "Mostrar área de trabalho"} onClick={toggleDesktop} />
      </footer>
    </div>
  );
}

function compiledAppForProgram(program: ProgramCatalogEntry) {
  const route = compiledRouteForProgram(program);
  const app = route ? appById(program.slug as DesktopAppId) : undefined;
  return app?.path === route ? app : undefined;
}

function initials(value?: string) {
  const parts = value?.trim().split(/\s+/).filter(Boolean) ?? [];
  return (parts.length > 1 ? `${parts[0][0]}${parts.at(-1)?.[0] ?? ""}` : parts[0]?.slice(0, 2) ?? "BS").toUpperCase();
}

function hasExternalFiles(dataTransfer: DataTransfer) {
  return Array.from(dataTransfer.types ?? []).includes("Files");
}
